import { useQuery } from '@sequencehq/api/utils'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import invariant from 'tiny-invariant'
import { Text, Skeleton, Flex } from '@sequencehq/core-components'
import {
  GreyGrey30,
  GreyGrey50,
  Primary13SemiBold,
  Primary13Regular
} from '@sequencehq/core-components'
import { toMoney } from '@sequencehq/utils'
import { ProgressBar } from 'RevenueRecognition/view/common/ProgressBar'
import { calculateProgressPercentages } from '../common/progressPercentages'

export const useDeferredRevenueDetailBreakdown = (props: {
  scheduleId: string
}) => {
  const scheduleQuery = useQuery(
    dashboardv99990101Client.getDeferredRevenueSchedule,
    { id: props.scheduleId },
    {
      select: res => {
        if (!res) {
          return null
        }

        const recognizedAmount = parseFloat(res.progress.recognized)
        const remainingAmount = parseFloat(res.progress.remaining)
        const originalAmount = parseFloat(res.progress.original)
        const percentages = calculateProgressPercentages({
          original: originalAmount,
          recognized: recognizedAmount,
          remaining: remainingAmount
        })

        return {
          id: res.id,
          progress: {
            currency: res.progress.currency,
            recognized: {
              percentage: percentages.recognized,
              amount: recognizedAmount
            },
            remaining: {
              percentage: percentages.remaining,
              amount: remainingAmount
            }
          }
        }
      }
    }
  )

  /**
   * We throw this error to be caught by the appropriate error boundary.
   */
  if (scheduleQuery.error) {
    throw new Error('Schedule summary could not be loaded')
  }

  if (scheduleQuery.isPending) {
    return {
      isLoading: true,
      summary: null
    }
  }

  invariant(scheduleQuery.data, 'Summary should be defined, if loaded')

  return {
    isLoading: false,
    summary: scheduleQuery.data
  }
}

export const DeferredRevenueDetailDrawerBreakdown = ({
  defRevScheduleId
}: {
  defRevScheduleId: string
}) => {
  const { isLoading, summary } = useDeferredRevenueDetailBreakdown({
    scheduleId: defRevScheduleId
  })

  if (isLoading) {
    return <Skeleton height="96px" width="100%" />
  }

  return (
    <Flex flexDirection="column">
      <Flex pt="4px" pb="8px">
        <Text {...Primary13SemiBold}>Breakdown</Text>
      </Flex>

      {summary && (
        <>
          <Flex py="8px" justifyContent="space-between" alignItems="center">
            <Text {...Primary13Regular}>Recognized as of today</Text>
            <Flex alignItems="center" gap="12px">
              <Text width="40px" textAlign="right" {...Primary13Regular}>
                {summary.progress.recognized.percentage}%
              </Text>
              <ProgressBar
                width="56px"
                height={8}
                percentage={summary.progress.recognized.percentage}
              />
              <Text minWidth="80px" textAlign="right" {...Primary13Regular}>
                {toMoney({
                  value: `${summary.progress.recognized.amount}`,
                  currency: summary.progress.currency
                })}
              </Text>
            </Flex>
          </Flex>

          <Flex
            py="8px"
            justifyContent="space-between"
            alignItems="center"
            borderTop="1px solid"
            borderColor={GreyGrey30}
          >
            <Text {...Primary13Regular}>Remaining balance</Text>
            <Flex alignItems="center" gap="12px">
              <Text width="40px" textAlign="right" {...Primary13Regular}>
                {summary.progress.remaining.percentage}%
              </Text>
              <ProgressBar
                width="56px"
                height={8}
                percentage={summary.progress.remaining.percentage}
              />
              <Text minWidth="80px" textAlign="right" {...Primary13Regular}>
                {toMoney({
                  value: `${summary.progress.remaining.amount}`,
                  currency: summary.progress.currency
                })}
              </Text>
            </Flex>
          </Flex>

          <Flex
            py="8px"
            justifyContent="space-between"
            alignItems="center"
            borderTop="1px solid"
            borderColor={GreyGrey50}
          >
            <Text {...Primary13SemiBold} fontWeight="semibold">
              Total
            </Text>
            <Text {...Primary13SemiBold} fontWeight="semibold">
              {toMoney({
                value: `${summary.progress.recognized.amount + summary.progress.remaining.amount}`,
                currency: summary.progress.currency
              })}
            </Text>
          </Flex>
        </>
      )}
    </Flex>
  )
}
